<script setup lang="ts">
import { PropertyType } from '@/backend/types'
import { useProperty } from '@/modules/Project/useProperty'
import { Color } from '@/shared/Color'
import { isSelectProperty } from '@/shared/utils/typeGuards'
import ListMenuItem from '@/uiKit/ListMenuItem.vue'
import { computed, onMounted, ref } from 'vue'
import SelectTypeOption from './SelectTypeOption.vue'
import SelectTypeOptionsFallback from './SelectTypeOptionsFallback.vue'

const emit = defineEmits<{
  insert: []
}>()

const propertyStore = useProperty()
const tempValue = ref('')
const optionsRef = ref<HTMLElement>()

export type Option = {
  value: string
  color?: string | null | undefined
  tool_fallback?: boolean | null | undefined
}

export type Item = {
  id: string
  data: Option
}

const resetValues = () => {
  tempValue.value = ''
}

const filteredOptions = computed(() => {
  const options = propertyStore.visibleOptions.filter((option) =>
    option.value.toLowerCase().includes(tempValue.value.toLowerCase()),
  )
  return options
})

const suggestToCreateText = computed(() => {
  if (propertyStore.editedProperty?.type === PropertyType.user_select) {
    return ''
  }

  const inputValues = parseOptionValues(tempValue.value)
  if (inputValues.length === 0) {
    return ''
  }

  const existingValues = propertyStore.visibleOptions.map((v) => v.value)
  if (inputValues.length === 1 && inputValues[0] && existingValues.includes(inputValues[0])) {
    return ''
  }

  return `Create "${inputValues.join(`", "`)}"`
})

const createNewOption = (value: string) => {
  if (!value) return
  const textValues = parseOptionValues(value)
  for (const value of textValues) {
    propertyStore.createSelectOption(createOption(value))
  }
  resetValues()
  emit('insert')
}

const placeholderComponent = ref()

onMounted(() => {
  resetValues()
})

const isOptionMarkedAsDefault = (option: Option) => {
  if (!propertyStore.isDirty) {
    if (!propertyStore.editedProperty) return false
    if (!('config' in propertyStore.editedProperty)) return false
    if (
      propertyStore.editedProperty.config &&
      'defaultOption' in propertyStore.editedProperty.config
    ) {
      return propertyStore.editedProperty.config.defaultOption === option.value
    }
  }
  return isSelectProperty(propertyStore.editedProperty)
    ? propertyStore.editedProperty.config.defaultOption === option.value
    : false
}

function onPaste(e: ClipboardEvent) {
  if (e.target instanceof HTMLInputElement && e.target.value) {
    return
  }
  const text = e.clipboardData?.getData('text') || ''
  const values = parseOptionValues(text)
  if (!values.length) {
    return
  }

  e.preventDefault()
  for (const option of values) {
    propertyStore.createSelectOption(createOption(option))
  }
  emit('insert')
}

/** Splits text by newlines and commas to get trimmed option texts */
function parseOptionValues(fromText: string): string[] {
  const values: string[] = []
  const parts = fromText.split(/[\n,]+/)
  for (const option of parts) {
    const trimmed = option.trim()
    // Array.prototype.includes is faster than Set for small arrays
    if (trimmed && !values.includes(trimmed)) {
      values.push(trimmed)
    }
  }
  return values
}

/** Creates an {@link Option} out of given value and auto-generated color */
function createOption(value: string): Option {
  return { value, color: Color[(Math.random() * Color.length) | 0] }
}
</script>

<template>
  <div>
    <SelectTypeOption
      ref="placeholderComponent"
      :property-id="propertyStore.editedProperty?.id"
      :property-type="propertyStore.editedProperty?.type"
      :value="tempValue"
      class="w-full"
      :show-advanced="false"
      placeholder="Search or add options"
      aria-label="Search or add options"
      @update="tempValue = $event.value"
      @create="createNewOption($event.value)"
      @paste="onPaste"
    />
    <div
      ref="optionsRef"
      class="flex flex-col items-start justify-start"
      v-bind="$attrs"
    >
      <SelectTypeOption
        v-for="(option, idx) in filteredOptions"
        :key="idx"
        :property-id="propertyStore.editedProperty?.id"
        :property-type="propertyStore.editedProperty?.type"
        :property-config="
          propertyStore.editedProperty &&
          'config' in propertyStore.editedProperty &&
          propertyStore.editedProperty.config &&
          'defaultOption' in propertyStore.editedProperty.config
            ? propertyStore.editedProperty.config
            : undefined
        "
        :is-default="isOptionMarkedAsDefault(option)"
        :is-manual="propertyStore.editedProperty?.tool === 'manual'"
        :value="option.value"
        :color="option.color ?? null"
        class="w-full"
        @update="propertyStore.updateSelectOption($event, option.value)"
        @delete="propertyStore.deleteSelectOption(option.value)"
        @mark-as-default="propertyStore.markOptionAsDefault(option.value)"
        @unmark-as-default="propertyStore.markOptionAsDefault(null)"
      />
      <ListMenuItem
        v-if="suggestToCreateText"
        :label="suggestToCreateText"
        aria-label="Create new option"
        :active="true"
        icon="plus"
        @click.prevent="(createNewOption(tempValue), placeholderComponent.focus())"
      />

      <template v-if="propertyStore.editedProperty?.type !== PropertyType.user_select">
        <h4 class="p-2 text-sm-12px-default text-text-subtlest">If no match found</h4>
        <SelectTypeOptionsFallback :filtered-options="filteredOptions" />
        <hr class="!col-[1/4] mx-[-4px] my-1 border-border-subtle" />
      </template>
    </div>
  </div>
</template>
